import React, { useState, useEffect } from 'react';
import { 
  Award, CalendarDays, Megaphone, Plus, Trash2, Search,
  RefreshCw, X, Check, User, Users,
  Info, Zap, UserPlus, UserMinus, Sparkles, Cake, CalendarOff,
  PenTool, Table as TableIcon, Database, CheckCircle, Lightbulb,
  PartyPopper, Hand, Calendar, Star, Palmtree, Trophy
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { formatDate, getTodayLocal } from '../lib/dateUtils';

export default function AdminNoticeManagement({ api }) {
  // Shoutouts state
  const [shoutouts, setShoutouts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showShoutoutModal, setShowShoutoutModal] = useState(false);
  const [shoutoutForm, setShoutoutForm] = useState({ employee_ids: [], message: '' });
  const [employeeSearch, setEmployeeSearch] = useState('');
  
  // Company Events state
  const [companyEvents, setCompanyEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState({ 
    title: '', 
    description: '', 
    event_date: '', 
    notice_type: 'event' 
  });
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [shoutoutsRes, eventsRes, employeesRes] = await Promise.all([
        api.get('/api/notices/shoutouts'),
        api.get('/api/notices/company-events'),
        api.get('/api/notices/employees-list')
      ]);
      
      setShoutouts(shoutoutsRes.data?.shoutouts || []);
      setCompanyEvents(eventsRes.data?.company_events || []);
      setEmployees(employeesRes.data?.employees || []);
    } catch (error) {
      console.error('Failed to fetch notice data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Shoutout handlers
  const handleCreateShoutout = async () => {
    if (shoutoutForm.employee_ids.length === 0 || !shoutoutForm.message.trim()) {
      toast.error('Please select at least one employee and enter a message');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/api/notices/shoutouts', shoutoutForm);
      toast.success(`Recognition posted for ${shoutoutForm.employee_ids.length} employee${shoutoutForm.employee_ids.length > 1 ? 's' : ''}!`);
      setShowShoutoutModal(false);
      setShoutoutForm({ employee_ids: [], message: '' });
      setEmployeeSearch('');
      fetchData();
    } catch (error) {
      toast.error(`Failed to create shoutout: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteShoutout = async (id) => {
    try {
      await api.delete(`/api/notices/shoutouts/${id}`);
      toast.success('Shoutout removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove shoutout');
    }
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (empId) => {
    setShoutoutForm(f => ({
      ...f,
      employee_ids: f.employee_ids.includes(empId)
        ? f.employee_ids.filter(id => id !== empId)
        : [...f.employee_ids, empId]
    }));
  };

  // Remove selected employee
  const removeSelectedEmployee = (empId) => {
    setShoutoutForm(f => ({
      ...f,
      employee_ids: f.employee_ids.filter(id => id !== empId)
    }));
  };

  // Company Event handlers
  const handleCreateEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.event_date) {
      toast.error('Please enter a title and date');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/api/notices/company-events', eventForm);
      toast.success('Company event created successfully!');
      setShowEventModal(false);
      setEventForm({ title: '', description: '', event_date: '', notice_type: 'event' });
      fetchData();
    } catch (error) {
      toast.error(`Failed to create event: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await api.delete(`/api/notices/company-events/${id}`);
      toast.success('Company event removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove event');
    }
  };

  // Filter employees for search
  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    emp.department?.toLowerCase().includes(employeeSearch.toLowerCase())
  );

  // Get selected employees details
  const selectedEmployees = employees.filter(e => shoutoutForm.employee_ids.includes(e._id));

  // Note: `formatDate` is imported from '../lib/dateUtils' — do NOT redefine it here
  // (a previous local wrapper caused infinite recursion → RangeError → page crash).

  // Get display name for shoutout (handles both single and multi-employee)
  const getShoutoutEmployeeDisplay = (shoutout) => {
    if (shoutout.employees && shoutout.employees.length > 1) {
      return `${shoutout.employees[0].name} + ${shoutout.employees.length - 1} more`;
    }
    return shoutout.employee_name;
  };

  return (
    <div className="space-y-6" data-testid="admin-notice-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[#1a1a2e]">Manage Notices</h2>
          <p className="text-sm text-neutral-500 mt-0.5">Create recognition posts and company announcements</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shoutouts / Recognition Section */}
        <Card className="border border-amber-200">
          <CardHeader className="bg-amber-50 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                Recognition / Shoutouts
              </CardTitle>
              <Button 
                size="sm" 
                className="bg-amber-600 hover:bg-amber-700"
                onClick={() => setShowShoutoutModal(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                New Shoutout
              </Button>
            </div>
            <p className="text-xs text-amber-700 mt-1">Expires after 2 days automatically</p>
          </CardHeader>
          <CardContent className="p-4 max-h-96 overflow-y-auto">
            {shoutouts.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Award className="w-10 h-10 mx-auto mb-2 text-amber-300" />
                <p className="text-sm">No active shoutouts</p>
                <p className="text-xs mt-1">Create one to recognize your team!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {shoutouts.map((shoutout) => (
                  <div 
                    key={shoutout._id}
                    className="p-3 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-100"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {/* Show multiple avatars if multiple employees */}
                        {shoutout.employees && shoutout.employees.length > 1 ? (
                          <div className="flex -space-x-2">
                            {shoutout.employees.slice(0, 3).map((emp, idx) => (
                              <div 
                                key={idx}
                                className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-semibold text-sm border-2 border-white"
                                title={emp.name}
                              >
                                {emp.name?.charAt(0)}
                              </div>
                            ))}
                            {shoutout.employees.length > 3 && (
                              <div className="w-8 h-8 rounded-full bg-amber-300 flex items-center justify-center text-amber-800 font-semibold text-xs border-2 border-white">
                                +{shoutout.employees.length - 3}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 font-semibold text-sm">
                            {shoutout.employee_name?.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#1a1a2e]">
                            {shoutout.employees && shoutout.employees.length > 1 
                              ? shoutout.employees.map(e => e.name).join(', ')
                              : shoutout.employee_name
                            }
                          </p>
                          <p className="text-xs text-neutral-500">
                            {shoutout.employees && shoutout.employees.length > 1 
                              ? `${shoutout.employees.length} team members`
                              : shoutout.employee_designation
                            }
                          </p>
                          <p className="text-sm text-amber-800 mt-1 italic">&quot;{shoutout.message}&quot;</p>
                          <p className="text-xs text-neutral-400 mt-1">
                            Expires: {formatDate(shoutout.expires_at)}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-neutral-400 hover:text-red-500"
                        onClick={() => handleDeleteShoutout(shoutout._id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Events Section */}
        <Card className="border border-indigo-200">
          <CardHeader className="bg-indigo-50 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                Company Events
              </CardTitle>
              <Button 
                size="sm" 
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => setShowEventModal(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                New Event
              </Button>
            </div>
            <p className="text-xs text-indigo-700 mt-1">Shown 7 days before event date</p>
          </CardHeader>
          <CardContent className="p-4 max-h-96 overflow-y-auto">
            {companyEvents.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <CalendarDays className="w-10 h-10 mx-auto mb-2 text-indigo-300" />
                <p className="text-sm">No upcoming events</p>
                <p className="text-xs mt-1">Create one to announce to your team!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {companyEvents.map((event) => (
                  <div 
                    key={event._id}
                    className="p-3 rounded-xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="w-8 h-8 rounded-xl bg-indigo-200 flex items-center justify-center text-indigo-700">
                          {event.notice_type === 'town_hall' ? (
                            <Megaphone className="w-4 h-4" />
                          ) : (
                            <CalendarDays className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold text-[#1a1a2e]">{event.title}</p>
                            <Badge variant="outline" className="text-xs capitalize">
                              {event.notice_type?.replace('_', ' ')}
                            </Badge>
                          </div>
                          {event.description && (
                            <p className="text-xs text-neutral-600 mt-0.5">{event.description}</p>
                          )}
                          <p className="text-xs text-indigo-600 font-medium mt-1">
                            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(event.event_date)}</span>
                            {event.days_until !== null && (
                              <span className="ml-2 text-neutral-500">
                                ({event.days_until === 0 ? 'Today!' : event.days_until === 1 ? 'Tomorrow' : `In ${event.days_until} days`})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-neutral-400 hover:text-red-500"
                        onClick={() => handleDeleteEvent(event._id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Shoutout Creation Modal - Multi-Select */}
      <Dialog open={showShoutoutModal} onOpenChange={setShowShoutoutModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              Create Recognition
            </DialogTitle>
            <DialogDescription>
              Give a shoutout to recognize employees&apos; great work. Select one or multiple team members. It will be visible on everyone&apos;s dashboard for 2 days.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Selected Employees Display */}
            {selectedEmployees.length > 0 && (
              <div>
                <label className="text-sm font-medium text-neutral-700 mb-2 block">
                  Selected ({selectedEmployees.length})
                </label>
                <div className="flex flex-wrap gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                  {selectedEmployees.map((emp) => (
                    <Badge 
                      key={emp._id}
                      className="bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer pr-1 flex items-center gap-1"
                      onClick={() => removeSelectedEmployee(emp._id)}
                    >
                      {emp.name}
                      <X className="w-3 h-3 ml-1" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Employee Search & Selection */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                {selectedEmployees.length === 0 ? 'Select Employee(s)' : 'Add More Employees'}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  placeholder="Search employees..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              <div className="mt-2 max-h-48 overflow-y-auto border rounded-xl">
                {filteredEmployees.length === 0 ? (
                  <div className="p-4 text-center text-neutral-500 text-sm">
                    No employees found
                  </div>
                ) : (
                  filteredEmployees.slice(0, 20).map((emp) => {
                    const isSelected = shoutoutForm.employee_ids.includes(emp._id);
                    return (
                      <div
                        key={emp._id}
                        role="button"
                        tabIndex={0}
                        aria-pressed={isSelected}
                        className={`w-full p-2 text-left hover:bg-white/60 flex items-center gap-3 border-b last:border-b-0 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-300 ${
                          isSelected ? 'bg-amber-50' : ''
                        }`}
                        onClick={() => toggleEmployeeSelection(emp._id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleEmployeeSelection(emp._id);
                          }
                        }}
                      >
                        <Checkbox 
                          checked={isSelected}
                          onCheckedChange={() => toggleEmployeeSelection(emp._id)}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                        />
                        <div className="w-7 h-7 rounded-full bg-neutral-200 flex items-center justify-center text-neutral-600 text-xs font-medium">
                          {emp.name?.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{emp.name}</p>
                          <p className="text-xs text-neutral-500 truncate">{emp.designation} • {emp.department}</p>
                        </div>
                        {isSelected && (
                          <Check className="w-4 h-4 text-amber-600" />
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
            
            {/* Message */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Recognition Message
              </label>
              <Textarea
                placeholder="Write a message recognizing their achievement or contribution..."
                value={shoutoutForm.message}
                onChange={(e) => setShoutoutForm(f => ({ ...f, message: e.target.value }))}
                rows={4}
              />
              <p className="text-xs text-neutral-400 mt-1">
                This will be visible to everyone on the notice board
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowShoutoutModal(false);
              setShoutoutForm({ employee_ids: [], message: '' });
              setEmployeeSearch('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateShoutout}
              disabled={submitting || shoutoutForm.employee_ids.length === 0 || !shoutoutForm.message.trim()}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
              {shoutoutForm.employee_ids.length > 1 
                ? `Recognize ${shoutoutForm.employee_ids.length} People`
                : 'Post Recognition'
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Event Creation Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-indigo-600" />
              Create Company Event
            </DialogTitle>
            <DialogDescription>
              Create an event or announcement that will appear on everyone&apos;s notice board 7 days before the event date.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Event Title */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Event Title *
              </label>
              <Input
                placeholder="e.g., Q1 Town Hall Meeting"
                value={eventForm.title}
                onChange={(e) => setEventForm(f => ({ ...f, title: e.target.value }))}
              />
            </div>
            
            {/* Event Type */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Event Type
              </label>
              <Select 
                value={eventForm.notice_type} 
                onValueChange={(val) => setEventForm(f => ({ ...f, notice_type: val }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="event">Event</SelectItem>
                  <SelectItem value="town_hall">Town Hall</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Event Date */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Event Date *
              </label>
              <Input
                type="date"
                value={eventForm.event_date}
                onChange={(e) => setEventForm(f => ({ ...f, event_date: e.target.value }))}
                min={getTodayLocal()}
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="text-sm font-medium text-neutral-700 mb-2 block">
                Description (Optional)
              </label>
              <Textarea
                placeholder="Add more details about the event..."
                value={eventForm.description}
                onChange={(e) => setEventForm(f => ({ ...f, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateEvent}
              disabled={submitting || !eventForm.title.trim() || !eventForm.event_date}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CalendarDays className="w-4 h-4 mr-2" />}
              Create Event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notice Types Documentation */}
      <Card className="mt-8 border border-white/40">
        <CardHeader className="bg-white/40 pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Info className="w-5 h-5 text-neutral-600" />
            Notice Types & Automation Rules
          </CardTitle>
          <p className="text-xs text-neutral-600 mt-1">Understanding how the Notice Board system works</p>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-8">
            
            {/* Section 1: Automated Notices */}
            <div>
              <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Automated Notices
              </h3>
              <p className="text-sm text-neutral-600 mb-4">
                These notices are automatically generated based on employee data. No manual action required.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* New Joiners */}
                <div className="p-4 rounded-xl bg-green-50 border border-green-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-200 flex items-center justify-center flex-shrink-0">
                      <UserPlus className="w-5 h-5 text-green-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-green-900 inline-flex items-center gap-1.5"><PartyPopper className="w-4 h-4" /> New Joiners</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-green-800"><span className="font-medium">Trigger:</span> Employee&apos;s first_working_day</p>
                        <p className="text-green-800"><span className="font-medium">Display Window:</span> 7 days after joining</p>
                        <p className="text-green-800"><span className="font-medium">Timezone:</span> IST (Indian Standard Time)</p>
                        <p className="text-green-800"><span className="font-medium">Data Source:</span> employment.first_working_day field</p>
                        <p className="text-green-700 mt-2"><span className="font-medium">Example:</span> If someone joins on Jan 15, they appear on the notice board from Jan 15-22</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Departures */}
                <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-200 flex items-center justify-center flex-shrink-0">
                      <UserMinus className="w-5 h-5 text-orange-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900 inline-flex items-center gap-1.5"><Hand className="w-4 h-4" /> Departures / Farewells</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-orange-800"><span className="font-medium">Trigger:</span> Employee&apos;s last_working_day is set</p>
                        <p className="text-orange-800"><span className="font-medium">Display Window:</span> 3 days before last day</p>
                        <p className="text-orange-800"><span className="font-medium">Timezone:</span> IST (Indian Standard Time)</p>
                        <p className="text-orange-800"><span className="font-medium">Data Source:</span> employment.last_working_day field</p>
                        <p className="text-orange-800"><span className="font-medium">Farewell Message:</span> Custom or auto-generated quirky message</p>
                        <p className="text-orange-700 mt-2"><span className="font-medium">Example:</span> Last day is Jan 20 → appears Jan 17-20</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Work Anniversaries */}
                <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-200 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-purple-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900 inline-flex items-center gap-1.5"><Trophy className="w-4 h-4" /> Work Anniversaries</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-purple-800"><span className="font-medium">Trigger:</span> Today&apos;s date matches join date (month & day)</p>
                        <p className="text-purple-800"><span className="font-medium">Display Window:</span> On the exact anniversary day only</p>
                        <p className="text-purple-800"><span className="font-medium">Timezone:</span> IST (Indian Standard Time)</p>
                        <p className="text-purple-800"><span className="font-medium">Data Source:</span> employment.first_working_day field</p>
                        <p className="text-purple-800"><span className="font-medium">Minimum:</span> 1+ years (first day doesn&apos;t count)</p>
                        <p className="text-purple-700 mt-2"><span className="font-medium">Shows:</span> &quot;X years of service&quot; badge</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Birthdays */}
                <div className="p-4 rounded-xl bg-pink-50 border border-pink-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-pink-200 flex items-center justify-center flex-shrink-0">
                      <Cake className="w-5 h-5 text-pink-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-pink-900 inline-flex items-center gap-1.5"><Cake className="w-4 h-4" /> Birthdays</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-pink-800"><span className="font-medium">Trigger:</span> Today&apos;s date matches birth date (month & day)</p>
                        <p className="text-pink-800"><span className="font-medium">Display Window:</span> On the exact birthday only</p>
                        <p className="text-pink-800"><span className="font-medium">Timezone:</span> IST (Indian Standard Time)</p>
                        <p className="text-pink-800"><span className="font-medium">Data Source:</span> personal.date_of_birth field</p>
                        <p className="text-pink-800"><span className="font-medium">Requirement:</span> Date of birth must be set in employee profile</p>
                        <p className="text-pink-700 mt-2"><span className="font-medium">Privacy:</span> Only shows name, not age</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Absences */}
                <div className="p-4 rounded-xl bg-sky-50 border border-sky-200 md:col-span-2">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-200 flex items-center justify-center flex-shrink-0">
                      <CalendarOff className="w-5 h-5 text-sky-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-sky-900 inline-flex items-center gap-1.5"><Palmtree className="w-4 h-4" /> Team Absences (Who&apos;s Out)</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-sky-800"><span className="font-medium">Trigger:</span> Approved leave requests</p>
                        <p className="text-sky-800"><span className="font-medium">Display:</span> Shows who&apos;s on leave today and tomorrow</p>
                        <p className="text-sky-800"><span className="font-medium">Visibility:</span> Same-department colleagues only (Admins see all)</p>
                        <p className="text-sky-800"><span className="font-medium">Data Source:</span> Leave management system (approved leaves)</p>
                        <p className="text-sky-800"><span className="font-medium">Leave Types Shown:</span> Casual, Sick, Earned, WFH, Optional</p>
                        <p className="text-sky-700 mt-2"><span className="font-medium">Updates:</span> Real-time as leave requests are approved/cancelled</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Manual Notices */}
            <div className="border-t border-white/40 pt-6">
              <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                <PenTool className="w-5 h-5 text-indigo-500" />
                Manual Notices (Admin Created)
              </h3>
              <p className="text-sm text-neutral-600 mb-4">
                These notices are created manually by admins and appear on all employee dashboards.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                {/* Shoutouts */}
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center flex-shrink-0">
                      <Award className="w-5 h-5 text-amber-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-amber-900 inline-flex items-center gap-1.5"><Star className="w-4 h-4" /> Recognition / Shoutouts</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-amber-800"><span className="font-medium">Purpose:</span> Publicly recognize employee achievements</p>
                        <p className="text-amber-800"><span className="font-medium">Created By:</span> Admins only</p>
                        <p className="text-amber-800"><span className="font-medium">Auto-Expiry:</span> 2 days after creation</p>
                        <p className="text-amber-800"><span className="font-medium">Selection:</span> Single or multiple employees</p>
                        <p className="text-amber-800"><span className="font-medium">Content:</span> Custom recognition message</p>
                        <p className="text-amber-800"><span className="font-medium">Visibility:</span> All employees see on their dashboard</p>
                        <p className="text-amber-700 mt-2"><span className="font-medium">Tip:</span> Use for project completions, exceptional work, milestones</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Events */}
                <div className="p-4 rounded-xl bg-indigo-50 border border-indigo-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-200 flex items-center justify-center flex-shrink-0">
                      <CalendarDays className="w-5 h-5 text-indigo-700" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-indigo-900 inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Company Events</h4>
                      <div className="mt-2 space-y-1 text-sm">
                        <p className="text-indigo-800"><span className="font-medium">Purpose:</span> Announce upcoming company events</p>
                        <p className="text-indigo-800"><span className="font-medium">Created By:</span> Admins only</p>
                        <p className="text-indigo-800"><span className="font-medium">Display Window:</span> 7 days before event date</p>
                        <p className="text-indigo-800"><span className="font-medium">Event Types:</span> Event, Town Hall, Announcement</p>
                        <p className="text-indigo-800"><span className="font-medium">Auto-Hide:</span> After event date passes</p>
                        <p className="text-indigo-800"><span className="font-medium">Content:</span> Title, Description, Date</p>
                        <p className="text-indigo-700 mt-2"><span className="font-medium">Shows:</span> &quot;In X days&quot; countdown on employee dashboard</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Quick Reference Table */}
            <div className="border-t border-white/40 pt-6">
              <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                <TableIcon className="w-5 h-5 text-neutral-600" />
                Quick Reference: Timing Rules
              </h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-neutral-100">
                      <th className="border border-white/40 px-4 py-2 text-left font-semibold text-neutral-700">Notice Type</th>
                      <th className="border border-white/40 px-4 py-2 text-left font-semibold text-neutral-700">Automation</th>
                      <th className="border border-white/40 px-4 py-2 text-left font-semibold text-neutral-700">Display Window</th>
                      <th className="border border-white/40 px-4 py-2 text-left font-semibold text-neutral-700">Data Required</th>
                      <th className="border border-white/40 px-4 py-2 text-left font-semibold text-neutral-700">Can Delete?</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="hover:bg-white/60">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><PartyPopper className="w-4 h-4 text-green-600" /> New Joiners</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-green-100 text-green-800">Auto</Badge></td>
                      <td className="border border-white/40 px-4 py-2">7 days after join date</td>
                      <td className="border border-white/40 px-4 py-2">first_working_day</td>
                      <td className="border border-white/40 px-4 py-2 text-neutral-500">No (auto expires)</td>
                    </tr>
                    <tr className="hover:bg-white/60">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Hand className="w-4 h-4 text-amber-600" /> Departures</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-green-100 text-green-800">Auto</Badge></td>
                      <td className="border border-white/40 px-4 py-2">3 days before last day</td>
                      <td className="border border-white/40 px-4 py-2">last_working_day</td>
                      <td className="border border-white/40 px-4 py-2 text-neutral-500">Cancel departure to remove</td>
                    </tr>
                    <tr className="hover:bg-white/60">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Trophy className="w-4 h-4 text-purple-600" /> Work Anniversary</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-green-100 text-green-800">Auto</Badge></td>
                      <td className="border border-white/40 px-4 py-2">Exact day only</td>
                      <td className="border border-white/40 px-4 py-2">first_working_day</td>
                      <td className="border border-white/40 px-4 py-2 text-neutral-500">No (1 day only)</td>
                    </tr>
                    <tr className="hover:bg-white/60">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Cake className="w-4 h-4 text-pink-600" /> Birthdays</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-green-100 text-green-800">Auto</Badge></td>
                      <td className="border border-white/40 px-4 py-2">Exact day only</td>
                      <td className="border border-white/40 px-4 py-2">date_of_birth</td>
                      <td className="border border-white/40 px-4 py-2 text-neutral-500">No (1 day only)</td>
                    </tr>
                    <tr className="hover:bg-white/60">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Palmtree className="w-4 h-4 text-sky-600" /> Team Absences</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-green-100 text-green-800">Auto</Badge></td>
                      <td className="border border-white/40 px-4 py-2">Today & Tomorrow</td>
                      <td className="border border-white/40 px-4 py-2">Approved leaves</td>
                      <td className="border border-white/40 px-4 py-2 text-neutral-500">Cancel leave to remove</td>
                    </tr>
                    <tr className="hover:bg-white/60 bg-amber-50/50">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-yellow-600" /> Shoutouts</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-amber-100 text-amber-800">Manual</Badge></td>
                      <td className="border border-white/40 px-4 py-2">2 days from creation</td>
                      <td className="border border-white/40 px-4 py-2">Admin creates</td>
                      <td className="border border-white/40 px-4 py-2 text-green-600 font-medium">Yes ✓</td>
                    </tr>
                    <tr className="hover:bg-white/60 bg-indigo-50/50">
                      <td className="border border-white/40 px-4 py-2 font-medium"><span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-indigo-600" /> Company Events</span></td>
                      <td className="border border-white/40 px-4 py-2"><Badge className="bg-indigo-100 text-indigo-800">Manual</Badge></td>
                      <td className="border border-white/40 px-4 py-2">7 days before event</td>
                      <td className="border border-white/40 px-4 py-2">Admin creates</td>
                      <td className="border border-white/40 px-4 py-2 text-green-600 font-medium">Yes ✓</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Section 4: Employee Data Requirements */}
            <div className="border-t border-white/40 pt-6">
              <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-neutral-600" />
                Employee Data Requirements for Automated Notices
              </h3>
              
              <div className="bg-white/40 rounded-xl p-4 border border-white/40">
                <p className="text-sm text-neutral-600 mb-4">
                  For automated notices to work correctly, ensure the following fields are filled in employee profiles:
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1a1a2e]">First Working Day</p>
                      <p className="text-xs text-neutral-500">Required for: New Joiners, Work Anniversaries</p>
                      <p className="text-xs text-neutral-400">Format: YYYY-MM-DD (e.g., 2024-01-15)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1a1a2e]">Date of Birth</p>
                      <p className="text-xs text-neutral-500">Required for: Birthday notices</p>
                      <p className="text-xs text-neutral-400">Format: YYYY-MM-DD (e.g., 1995-03-20)</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1a1a2e]">Last Working Day</p>
                      <p className="text-xs text-neutral-500">Required for: Departure/Farewell notices</p>
                      <p className="text-xs text-neutral-400">Set via Organisation → Employee → Set Departure</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-[#1a1a2e]">Department(s)</p>
                      <p className="text-xs text-neutral-500">Required for: Team Absences visibility filter</p>
                      <p className="text-xs text-neutral-400">Employees only see same-department absences</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5: Tips */}
            <div className="border-t border-white/40 pt-6">
              <h3 className="text-lg font-semibold text-[#1a1a2e] mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Tips & Best Practices
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                  <p className="text-sm text-yellow-900">
                    <span className="font-semibold">💡 Shoutout Tips:</span> Keep messages specific and genuine. Mention what the person did and its impact. Multiple-person shoutouts are great for team achievements.
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-900">
                    <span className="font-semibold">💡 Event Planning:</span> Create company events at least 7 days before so employees have time to plan. Use &quot;Town Hall&quot; type for all-hands meetings.
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-xl border border-green-200">
                  <p className="text-sm text-green-900">
                    <span className="font-semibold">💡 Data Quality:</span> Ensure all employees have their first_working_day and date_of_birth set during onboarding for automated celebrations to work.
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                  <p className="text-sm text-purple-900">
                    <span className="font-semibold">💡 Farewell Messages:</span> Custom farewell messages make departures more personal. If not set, a random quirky message is auto-generated.
                  </p>
                </div>
              </div>
            </div>
            
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
